import {
  zoraTokenCommunityClaimAbi,
  zoraTokenCommunityClaimAddress,
} from '@zoralabs/zora-token-contracts'
import { useMemo } from 'react'
import { Address, ContractFunctionParameters } from 'viem'
import { useReadContracts } from 'wagmi'
import { DEFAULT_CHAIN_ID } from './chains'

const buildGetClaimStatusCalls = (accounts: Address[]): ContractFunctionParameters[] => {
  const address = ZORA_TOKEN_CLAIM_CONTRACT_ADDRESS
  return accounts
    .map((account) => {
      const allocationCall: ContractFunctionParameters = {
        abi: zoraTokenCommunityClaimAbi,
        address,
        functionName: 'allocations',
        args: [account],
      }
      const hasClaimedCall: ContractFunctionParameters = {
        abi: zoraTokenCommunityClaimAbi,
        address,
        functionName: 'hasClaimed',
        args: [account],
      }

      return [allocationCall, hasClaimedCall]
    })
    .flatMap((call) => call)
}

/**
 * Hook to check claim status for multiple accounts
 * @param accounts Array of Ethereum addresses to check claim status for
 * @returns Array of objects containing:
 * - account: The Ethereum address
 * - allocation: The token allocation amount for this account (in wei)
 * - hasClaimed: Boolean indicating if the account has already claimed their allocation
 */
export const useClaimStatus = ({ accounts }: { accounts: Address[] }) => {
  const claimStatusCalls = useMemo(() => buildGetClaimStatusCalls(accounts), [accounts])

  // do multicall read to read both allocation and hasClaimed for all accounts
  const result = useReadContracts({
    contracts: claimStatusCalls,
    allowFailure: false,
  })

  const data = result.data

  if (result.error) {
    console.error(result.error)
  }

  if (!data) {
    return null
  }

  return accounts.map((account, index) => {
    const allocation = data[index * 2] as bigint
    const hasClaimed = data[index * 2 + 1] as boolean
    return {
      account,
      allocation,
      hasClaimed,
    }
  })
}

export const ZORA_TOKEN_CLAIM_CONTRACT_ADDRESS =
  zoraTokenCommunityClaimAddress[DEFAULT_CHAIN_ID]
